// src/Components/HighLevelComponents/Tool/renderers/FlashcardRenderer.tsx
import React, { useState, useContext } from "react";
import { ToolRendererProps, FlashcardResult } from "../types";
import { FaCopy } from "react-icons/fa";
import { HiOutlineEyeOff, HiOutlineEye } from "react-icons/hi";
import { BiExport } from "react-icons/bi";
import { MdOutlineQuiz } from "react-icons/md";
import toast from "react-hot-toast";
import { GoogleAnalyticsContext } from "../../../../Hooks/GoogleAnalyticsContext";

// Anki Logo SVG Component
const AnkiLogo = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="20"
    height="20"
    viewBox="0 0 50 50"
    className="mr-2"
  >
    <path d="M37.38,2H12.84C9.66,2,7,4.85,7,8v7.26c10.89,1.54,25.87,5.9,36,13.65V7.62C43,4.54,40.46,2,37.38,2z M38.93,13.68 c-0.06,0.38-0.34,0.68-0.7,0.79l-3,0.88l-0.97,2.98c-0.12,0.36-0.44,0.62-0.81,0.67c-0.05,0.01-0.1,0.01-0.14,0.01 c-0.33,0-0.64-0.16-0.83-0.43L30.71,16l-3.12-0.01c-0.38,0-0.73-0.22-0.9-0.56c-0.16-0.34-0.12-0.75,0.11-1.05l1.91-2.48 l-0.96-2.97C27.63,8.57,27.73,8.17,28,7.9c0.28-0.26,0.68-0.35,1.04-0.22l2.94,1.05l2.53-1.83c0.31-0.23,0.72-0.25,1.06-0.07 C35.9,7,36.11,7.36,36.1,7.74l-0.09,3.12l2.53,1.85C38.84,12.93,39,13.31,38.93,13.68z M7,17.27V42c0,1.86,0.92,3.39,2.09,4.41 C10.27,47.42,11.68,48,13,48h24.38c3.08,0,5.62-2.54,5.62-5.62V31.47C33.86,23.88,18.79,18.99,7,17.27z M30.71,31.57l-3.33,4.14 l1.52,5.09c0.16,0.55,0,1.13-0.42,1.51c-0.42,0.39-1.01,0.5-1.54,0.3l-4.96-1.89l-4.38,3.02C17.35,43.91,17.05,44,16.76,44 c-0.25,0-0.5-0.06-0.72-0.19c-0.5-0.28-0.79-0.8-0.77-1.37l0.27-5.31l-4.22-3.23c-0.45-0.34-0.67-0.9-0.56-1.46s0.52-1,1.07-1.15 l5.13-1.39l1.77-5.01c0.19-0.54,0.66-0.92,1.22-0.98c0.57-0.07,1.11,0.18,1.43,0.66l2.9,4.45l5.31,0.13 c0.57,0.02,1.08,0.35,1.32,0.86C31.14,30.53,31.07,31.13,30.71,31.57z" />
  </svg>
);

interface FlashcardProps {
  front: string;
  back: string;
  index: number;
}

// Enhanced Flashcard component with copy functionality
const Flashcard: React.FC<FlashcardProps> = ({ front, back, index }) => {
  const [showAnswer, setShowAnswer] = useState(false);
  const { logEvent } = useContext(GoogleAnalyticsContext);

  const toggleAnswer = () => {
    setShowAnswer(!showAnswer);
    logEvent(
      "FlashcardInteraction",
      !showAnswer ? "ShowAnswer" : "HideAnswer",
      `flashcard_${index + 1}`
    );
  };

  const copyToClipboard = async () => {
    try {
      const text = `Flashcard ${index + 1}:\nFront: ${front}\nBack: ${back}`;
      await navigator.clipboard.writeText(text);
      toast.success("Copied to clipboard!");
      logEvent("Export", "CopyFlashcardToClipboard", `flashcard_${index + 1}`);
    } catch (err) {
      toast.error("Failed to copy to clipboard");
      console.error("Failed to copy: ", err);
    }
  };

  return (
    <div className="border border-gray-200 rounded-lg bg-white shadow-sm overflow-hidden h-full flex flex-col">
      <div className="p-4 border-b border-gray-100 flex-grow">
        <div className="flex justify-between mb-2">
          <h3 className="font-bold text-lg text-gray-800">
            Flashcard {index + 1}
          </h3>
          <button
            onClick={copyToClipboard}
            className="p-1 text-gray-500 hover:text-gray-700 focus:outline-none"
            title="Copy to clipboard"
          >
            <FaCopy size={16} />
          </button>
        </div>
        <p className="text-gray-700">{front}</p>
      </div>

      <div className="p-4 bg-gray-50 flex flex-col">
        {showAnswer ? (
          <div>
            <p className="text-gray-700 mb-2">{back}</p>
            <button
              onClick={toggleAnswer}
              className="flex items-center text-sm text-gray-600 hover:text-gray-800"
            >
              <HiOutlineEyeOff className="mr-1" />
              Hide Answer
            </button>
          </div>
        ) : (
          <button
            onClick={toggleAnswer}
            className="py-2 px-4 bg-yellow-400 hover:bg-yellow-500 text-gray-800 text-sm font-medium rounded-md flex items-center justify-center"
          >
            <HiOutlineEye className="mr-1" />
            Show Answer
          </button>
        )}
      </div>
    </div>
  );
};

// Main renderer component with improved layout and export options
const FlashcardRenderer: React.FC<ToolRendererProps> = ({
  results,
  isLoading,
}) => {
  const { logEvent } = useContext(GoogleAnalyticsContext);
  const [exportMenuOpen, setExportMenuOpen] = useState(false);

  if (isLoading) {
    return <div className="text-center py-4">Loading flashcards...</div>;
  }

  if (!results || results.length === 0) {
    return <div>No flashcards to display</div>;
  }

  // Create Anki-compatible export
  const exportToAnki = () => {
    // Format: front,back
    const csvContent = results
      .map((result: FlashcardResult) => {
        // Clean and escape the content
        const front = result.front
          .replace(/"/g, '""') // Escape quotes
          .replace(/\n/g, "<br>") // Convert newlines to HTML breaks
          .trim();
        const back = result.back
          .replace(/"/g, '""') // Escape quotes
          .replace(/\n/g, "<br>") // Convert newlines to HTML breaks
          .trim();

        // Ensure each field is properly quoted
        return `"${front}","${back}"`;
      })
      .join("\n");

    // Add metadata headers only (no data header row)
    const finalCsv = `#separator:,
#html:true
#columns:front,back
${csvContent}`;

    // Create and download file
    const blob = new Blob([finalCsv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "flashcards_anki.csv";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);

    toast.success("Anki file downloaded!");
    logEvent("Export", "ExportToAnki", "flashcards");
    setExportMenuOpen(false);
  };

  // Export to Quizlet format
  const exportToQuizlet = () => {
    // Format: term\tdefinition
    const tsvContent = results
      .map((result: FlashcardResult) => {
        // Replace tabs and newlines
        const front = result.front.replace(/\t/g, " ").replace(/\n/g, " ");
        const back = result.back.replace(/\t/g, " ").replace(/\n/g, " ");
        return `${front}\t${back}`;
      })
      .join("\n");

    // Create and download file
    const blob = new Blob([tsvContent], {
      type: "text/tab-separated-values;charset=utf-8",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "flashcards_quizlet.txt";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);

    toast.success("Quizlet file downloaded!");
    logEvent("Export", "ExportToQuizlet", "flashcards");
    setExportMenuOpen(false);
  };

  // Copy all flashcards to clipboard
  const copyToClipboard = async () => {
    try {
      let text = "Flashcards:\n\n";

      results.forEach((result: FlashcardResult, index) => {
        text += `Card ${index + 1}:\n`;
        text += `Front: ${result.front}\n`;
        text += `Back: ${result.back}\n\n`;
      });

      await navigator.clipboard.writeText(text);
      toast.success("All flashcards copied to clipboard!");
      logEvent("Export", "CopyAllFlashcards", "flashcards");
      setExportMenuOpen(false);
    } catch (err) {
      toast.error("Failed to copy to clipboard");
      console.error("Failed to copy: ", err);
    }
  };

  // Export as text file
  const exportToText = () => {
    let text = "FLASHCARDS\n\n";

    results.forEach((result: FlashcardResult, index) => {
      text += `CARD ${index + 1}\n`;
      text += `Front: ${result.front}\n`;
      text += `Back: ${result.back}\n\n`;
    });

    const blob = new Blob([text], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "flashcards.txt";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);

    toast.success("Text file downloaded!");
    logEvent("Export", "ExportToText", "flashcards");
    setExportMenuOpen(false);
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h3 className="text-lg font-medium text-gray-700">
          {results.length} Flashcards Generated
        </h3>
        <div className="flex space-x-2">
          {/* Anki Export Button */}
          <button
            onClick={exportToAnki}
            className="px-4 py-2 bg-green-100 hover:bg-green-200 text-green-800 font-medium rounded-lg flex items-center transition-colors"
          >
            <AnkiLogo />
            Export to Anki
          </button>

          {/* Other Export Options */}
          <div className="relative">
            <button
              onClick={() => setExportMenuOpen(!exportMenuOpen)}
              className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium rounded-lg flex items-center transition-colors"
            >
              <BiExport className="mr-2" />
              More Export Options
            </button>

            {exportMenuOpen && (
              <div className="absolute right-0 mt-2 w-56 bg-white rounded-md shadow-lg z-10 overflow-hidden border border-gray-100">
                <div className="py-1">
                  <button
                    onClick={copyToClipboard}
                    className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 flex items-center"
                  >
                    <FaCopy className="mr-2" />
                    Copy all to clipboard
                  </button>
                  <button
                    onClick={exportToQuizlet}
                    className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 flex items-center"
                  >
                    <MdOutlineQuiz className="mr-2" />
                    Export for Quizlet
                  </button>
                  <button
                    onClick={exportToText}
                    className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 flex items-center"
                  >
                    <FaCopy className="mr-2" />
                    Export as Text
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {results.map((result: FlashcardResult, index) => (
          <Flashcard
            key={index}
            front={result.front}
            back={result.back}
            index={index}
          />
        ))}
      </div>

      <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
        <h4 className="font-medium text-blue-800 mb-2">Export Options</h4>
        <ul className="list-disc pl-5 text-sm text-blue-700 space-y-1">
          <li>
            <strong>Anki Export:</strong> Download a CSV file compatible with
            Anki's import format
          </li>
          <li>
            <strong>Quizlet Export:</strong> Download a TXT file that can be
            imported into Quizlet
          </li>
          <li>
            <strong>Text Export:</strong> Plain text format for all flashcards
          </li>
          <li>
            <strong>Copy to Clipboard:</strong> Copy all flashcards for pasting
            elsewhere
          </li>
        </ul>
      </div>
    </div>
  );
};

export default FlashcardRenderer;
